// Client-side parser for the field-length-prefixed GDT format used by German practice
// management software (GDT is a standard interchange format, not specific to one vendor).
//
// Field kennungen below are CONFIRMED against real Medical Office documentation + a
// working reference script (not the generic/documented GDT spec this file started from —
// that earlier version had 3106 wrong as "title" when it's actually the combined
// PLZ+city field, which is exactly why patient addresses were showing up in the wrong
// place). Still worth validating the exact byte-counting rule for the length prefix
// against a real exported file before fully trusting this in production, per the source
// notes — the format itself (see below) is correct per the GDT documentation example,
// just unconfirmed byte-for-byte against this practice's actual export yet.
//
// Line format: a 3-digit length prefix (total bytes in the line, including the prefix,
// the 4-digit kennung, the value, and the trailing CR/LF), then the kennung, then the
// value.

export interface GdtParseError extends Error {
    filename?: string;
}

function makeParseError(message: string, filename?: string): GdtParseError {
    const err = new Error(message) as GdtParseError;
    err.filename = filename;
    return err;
}

// Kennung dictionary — confirmed via partner documentation + reference script. Still no
// confirmed consent-field kennung — do not add one here until the partner confirms it
// (see the build plan's "Consent field" section).
const FK = {
    SATZART: "8000", // record type — expect 6301 or 6302 for a patient record
    RECORD_LENGTH_V2: "8100", // GDT 2.x
    RECORD_LENGTH_V3: "8004", // GDT 3.x
    ENCODING: "9206",
    GDT_VERSION: "9218",
    SENDER_ID: "8316", // GDT-ID of the sending program, when it writes one
    PATIENT_ID: "3000",
    LAST_NAME: "3101",
    FIRST_NAME: "3102",
    BIRTHDATE: "3103", // DDMMYYYY (variants: see parseGdtBirthDate)
    TITLE: "3104",
    INSURANCE_NUMBER: "3105",
    PLZ_CITY: "3106", // combined "PLZ Ort" field — NOT a title, see splitPlzCity
    STREET: "3107",
    GENDER: "3110",
    PLZ: "3112",  // alternative to 3106 (GDT 2.1/3.x, e.g. tomedo)
    CITY: "3113", // alternative to 3106
    EMAIL: "3619",     // GDT 3.1
    EMAIL_ALT: "3633", // tomedo's export
    // Different practice software sends phone under different, non-standard kennungen — 3618/
    // 3626 confirmed against one real "Medical Office" export, 3620/3621/3622 against another.
    // Collect all five rather than guessing which convention a given installation uses; a sender
    // that doesn't use a given kennung just contributes nothing from it.
    PHONE_GENERAL: "3620",
    PHONE_1: "3621",
    PHONE_2: "3622",
    PHONE_1_ALT: "3618",
    PHONE_2_ALT: "3626",
    INSURANCE_PROVIDER: "2002", // Kostenträger Langname
    // Guardian/billing-recipient block — confirmed against a real export (2026-09-23) where
    // a minor's record carried these alongside her own 3101-3110 fields, e.g. father's name
    // and address for a child patient. All of them feed the auto-created/pre-filled recipient;
    // the birth date and gender reach the consent questionnaire through it.
    RECIPIENT_LAST_NAME: "3201",
    RECIPIENT_FIRST_NAME: "3202",
    RECIPIENT_BIRTHDATE: "3203",
    RECIPIENT_CITY: "3204",
    RECIPIENT_STREET: "3205",
    RECIPIENT_PLZ: "3207",
    RECIPIENT_TITLE: "3208",
    RECIPIENT_GENDER: "3209", // same codes as 3110 — seen 1 on a father, 2 on a mother
} as const;

const VALID_SATZARTEN = new Set(["6301", "6302"]);

// ── Character set ────────────────────────────────────────────────────────────────────────────
// Decoded by our own tables, not TextDecoder("iso-8859-1"): that label is really windows-1252,
// and Node and browsers disagree on it (Chrome turns CP437 "ä" 0x84 into "„", Node keeps 0x84 —
// so jest passed while the browser sync garbled CP437 umlauts). The desktop agent's parser
// (agent/src/gdtParser.ts) has the same code, so both decode a file identically.

/** IBM code page 437, bytes 0x80–0xFF. */
const CP437_HIGH =
    "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒ" +
    "áíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐" +
    "└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀" +
    "αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ";

/** Windows-1252, bytes 0x80–0x9F (0xA0–0xFF equal ISO 8859-1, i.e. the byte's own code point). */
const CP1252_C1 =
    "€\u0081‚ƒ„…†‡ˆ‰Š‹Œ\u008dŽ\u008f" +
    "\u0090‘’“”•–—˜™š›œ\u009džŸ";

export type Charset = "cp437" | "cp1252" | "utf8";

interface GdtField {
    kennung: string;
    value: string;
}

/** GDT 2.1 has no code for UTF-8, but some practice software (e.g. medatixx) can be set to write it.
 *  A CP437 or CP1252 umlaut is a single byte above 0x7F, which is never valid UTF-8 on its own, so a
 *  file with such bytes that still decodes as strict UTF-8 is UTF-8. */
function isUtf8(bytes: Uint8Array): boolean {
    if (!bytes.some((b) => b > 0x7f)) return false;
    try {
        new TextDecoder("utf-8", { fatal: true }).decode(bytes);
        return true;
    } catch {
        return false;
    }
}

/** Bytes that prove nothing when the bytes have to decide (no 9206): letters in both sets (CP437
 *  è î Ä Ü = CP1252 Š Œ Ž š), CP1252 punctuation that real names and addresses contain (€ … ’ ‘ – —,
 *  e.g. "O’Brien", "Str. 5–7"), CP1252's non-breaking space 0xA0, and 0xE1 (CP437 ß, CP1252 á). */
const NO_EVIDENCE_BYTES = new Set([0x8a, 0x8c, 0x8e, 0x9a, 0x80, 0x85, 0x91, 0x92, 0x96, 0x97, 0xa0, 0xe1]);

/** How many bytes look like each set's letters: CP437 has them at 0x80–0x9A and 0xA1–0xA5
 *  (ä ö ü Ö é í …), where CP1252 has punctuation; CP1252 has them at 0xC0–0xFF, where CP437 has box
 *  drawing and Greek. */
function letterHits(bytes: Uint8Array): { cp437: number; cp1252: number } {
    let cp437 = 0;
    let cp1252 = 0;
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i];
        if (NO_EVIDENCE_BYTES.has(b)) continue;
        if ((b >= 0x80 && b <= 0x9a) || (b >= 0xa1 && b <= 0xa5)) cp437++;
        else if (isCp1252LetterByte(b)) cp1252++;
    }
    return { cp437, cp1252 };
}

/** What may overrule a declared 9206 — only bytes that can't be the declared set's text:
 *  - CP437 umlauts ü ä ö Ö (0x81 doesn't even exist in CP1252; 0x84/0x94/0x99 are „ ” ™ there).
 *    A lone 0x80 or 0x96 proves nothing: as likely the CP1252 "€" or "–" as the CP437 "Ç" or "û".
 *  - CP1252 letters À–ÿ (0xC0–0xFF), which are box drawing and Greek in CP437 and never in a name or
 *    address — except 0xE1 (CP437 ß, CP1252 á), which keeps the declaration. ×/÷ don't count. */
const CP437_UMLAUT_BYTES = new Set([0x81, 0x84, 0x94, 0x99]);
const isCp1252LetterByte = (b: number) => b >= 0xc0 && b !== 0xe1 && b !== 0xd7 && b !== 0xf7;

function hasCp437Umlaut(bytes: Uint8Array): boolean {
    for (let i = 0; i < bytes.length; i++) if (CP437_UMLAUT_BYTES.has(bytes[i])) return true;
    return false;
}

function hasCp1252Letter(bytes: Uint8Array): boolean {
    for (let i = 0; i < bytes.length; i++) if (isCp1252LetterByte(bytes[i])) return true;
    return false;
}

/** Field 9206 (GDT 2.1): 1 = 7-bit, 2 = CP437 (the spec's default), 3 = ISO 8859-1 / CP1252. GDT 3.x
 *  files often leave it out (tomedo does), and a sender can declare one set and write another, so:
 *  UTF-8 when the bytes are valid UTF-8; a declared 2 or 3 is kept unless the bytes can only be the
 *  other set (see CP437_UMLAUT_BYTES); without a declaration the letter ranges decide (letterHits).
 *  A tie — including a file whose only special byte is 0xE1 — goes to the version's usual set:
 *  CP437 for GDT 2.x (the spec's default, so 0xE1 = ß), CP1252 for GDT 3.x (so 0xE1 = á), which
 *  dropped 9206 and is written as ANSI/ISO in practice. */
function detectCharset(bytes: Uint8Array, declared: string | undefined, majorVersion: number | undefined): Charset {
    if (isUtf8(bytes)) return "utf8";
    if (declared === "2" || declared === "3") {
        const cp437 = hasCp437Umlaut(bytes);
        const cp1252 = hasCp1252Letter(bytes);
        if (declared === "2") return !cp437 && cp1252 ? "cp1252" : "cp437";
        return !cp1252 && cp437 ? "cp437" : "cp1252";
    }
    const hits = letterHits(bytes);
    if (hits.cp1252 !== hits.cp437) return hits.cp1252 > hits.cp437 ? "cp1252" : "cp437";
    return (majorVersion ?? 2) >= 3 ? "cp1252" : "cp437";
}

/** The GDT major version: 9218 ("02.10", "03.00", "03.50"); without it, the record-length field
 *  gives it away (8100 in 2.x, 8004 in 3.x), and 9206 only exists in 2.1. 1.x counts as 2. */
function gdtMajorVersion(fields: GdtField[]): number | undefined {
    const has = (kennung: string) => fields.some((f) => f.kennung === kennung);
    const declared = fields.find((f) => f.kennung === FK.GDT_VERSION)?.value.trim().match(/^0?(\d)/);
    if (declared) return Math.max(2, Number(declared[1]));
    if (has(FK.RECORD_LENGTH_V3)) return 3;
    if (has(FK.RECORD_LENGTH_V2) || has(FK.ENCODING)) return 2;
    return undefined;
}

/** A field value is one char per byte (see decodeFields); turn it into text. */
function decodeValue(raw: string, charset: Charset): string {
    if (charset === "utf8") return new TextDecoder("utf-8").decode(Uint8Array.from(raw, (c) => c.charCodeAt(0)));
    let out = "";
    for (let i = 0; i < raw.length; i++) {
        const code = raw.charCodeAt(i);
        if (code < 0x80) out += raw[i];
        else if (charset === "cp437") out += CP437_HIGH[code - 0x80];
        else out += code <= 0x9f ? CP1252_C1[code - 0x80] : raw[i];
    }
    return out;
}

/** Splits on a one-char-per-byte string first, because the length prefixes count bytes, then
 *  decodes every value with the file's character set. */
function decodeFields(buffer: ArrayBuffer, filename?: string): { fields: GdtField[]; charset: Charset } {
    const bytes = new Uint8Array(buffer);
    let raw = "";
    for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
    const fields = splitFields(raw, filename);
    const declared = fields.find((f) => f.kennung === FK.ENCODING)?.value.trim();
    const charset = detectCharset(bytes, declared, gdtMajorVersion(fields));
    return { fields: fields.map((f) => ({ ...f, value: decodeValue(f.value, charset) })), charset };
}

/** One field per line: 3-digit length, 4-digit field id, value. Split on the line breaks rather than
 *  by jumping the length: senders differ in what the length counts (CRLF as the spec says, a lone
 *  LF, or nothing) and some end the last line without a break. So the length is only a check: a line
 *  more than 2 bytes shorter than it says was cut off — e.g. a file the practice software is still
 *  writing — and the whole file is refused rather than importing a cut-off value. Blank lines and a
 *  DOS end-of-file mark (0x1A) are skipped. */
function splitFields(text: string, filename?: string): GdtField[] {
    const fields: GdtField[] = [];
    const lines = text.split(/\r\n|\r|\n/);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (/^[\s\x1a]*$/.test(line)) continue;

        const lengthStr = line.slice(0, 3);
        if (!/^\d{3}$/.test(lengthStr)) {
            throw makeParseError(`Malformed GDT line ${i + 1}: bad length prefix "${lengthStr}"`, filename);
        }
        const kennung = line.slice(3, 7);
        if (!/^\d{4}$/.test(kennung)) {
            throw makeParseError(`Malformed GDT line ${i + 1}: bad kennung "${kennung}"`, filename);
        }
        const declared = parseInt(lengthStr, 10);
        if (declared > line.length + 2) {
            throw makeParseError(`Truncated GDT file: line ${i + 1} should be ${declared} bytes, has ${line.length}`, filename);
        }
        fields.push({ kennung, value: line.slice(7) });
    }
    return fields;
}

export interface ParsedGdtPatient {
    externalId: string;
    firstName?: string;
    lastName: string;
    street?: string;
    plz?: string;
    city?: string;
    birthDate?: string; // ISO yyyy-mm-dd
    gender?: string;
    title?: string;
    insuranceNumber?: string;
    insuranceProvider?: string;
    email?: string;
    tel?: string[];
    /** Guardian/billing-recipient contact info, when the source record carries one
     *  (typically a parent on a minor's record) — see the RECIPIENT_* comment above.
     *  Absent when the record doesn't include a last name for it. */
    guardian?: {
        firstName?: string; lastName: string; street?: string; plz?: string; city?: string; title?: string;
        birthDate?: string; gender?: string;
    };
    /** What the file says about the program that wrote it — for support, never about the patient. */
    source?: GdtFileSource;
}

export interface GdtFileSource {
    /** 9218 as written, e.g. "02.10". */
    gdtVersion?: string;
    /** 9206 as written (2 = CP437, 3 = CP1252). */
    charsetDeclared?: string;
    /** What the file was actually decoded as (detectCharset). */
    charsetUsed: Charset;
    /** 8316, the sending program's GDT-ID. */
    senderId?: string;
}

/** Shared with the review popup and the sync context so "is this patient a minor" never
 *  drifts between the two call sites that need it (deciding whether to auto-derive a "self"
 *  recipient, and deciding whether to disable that option in the fallback picker). */
export function isMinorFromIso(iso?: string): boolean {
    if (!iso) return false;
    const birth = new Date(iso);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const beforeBirthdayThisYear = now.getMonth() < birth.getMonth() ||
        (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
    if (beforeBirthdayThisYear) age -= 1;
    return age < 18;
}

/** 3103 is TTMMJJJJ in every GDT version. Also accepted, because senders write them: JJJJMMTT (the
 *  date style GDT 3.x uses elsewhere), TTMMJJ (tomedo, e.g. "070248"), and any of them with dots,
 *  dashes or slashes. The 8-digit forms never collide: JJJJMMTT read as TTMMJJJJ has month 18–20.
 *  A two-digit year after this year's is the 1900s. Anything that isn't a real date between 1850
 *  and today gives undefined, never a guess (the old parser turned "00000000" into "0000-00-00"). */
export function parseGdtBirthDate(raw: string | undefined, today = new Date()): string | undefined {
    if (!raw) return undefined;
    const digits = raw.replace(/[.\-/ ]/g, "");
    if (!/^\d+$/.test(digits)) return undefined;
    const thisYear = today.getFullYear();
    const iso = (y: number, m: number, d: number): string | undefined => {
        const date = new Date(Date.UTC(y, m - 1, d));
        const real = date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
        // A day of slack: a baby born today must pass even while UTC is still on yesterday.
        if (!real || y < 1850 || date.getTime() > today.getTime() + 86_400_000) return undefined;
        return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    };
    const n = (from: number, to: number) => Number(digits.slice(from, to));
    if (digits.length === 8) return iso(n(4, 8), n(2, 4), n(0, 2)) ?? iso(n(0, 4), n(4, 6), n(6, 8));
    if (digits.length === 6) {
        const yy = n(4, 6);
        return iso(yy > thisYear % 100 ? 1900 + yy : 2000 + yy, n(2, 4), n(0, 2));
    }
    return undefined;
}

// FK 3110 (Geschlecht) is the GDT standard's own numeric code — 1 männlich, 2 weiblich,
// 3 divers (the newer revision; older exports may never send it; some practice software sends
// 0 for divers instead, seen in a practice's export 2026-10-19) — not the app's own
// "M"/"F"/"D" convention (CreatePatientModal.tsx/PatientPage.tsx's GENDER_OPTIONS), which
// is what a staff member actually sees/edits afterward on the patient's own page. Left as
// the raw code before this mapping existed, so every GDT-imported patient showed "1"/"2"
// there instead of a real gender. An already-lettered value (a re-sync after a manual
// edit, or a differently-configured PVS) passes through unchanged rather than being
// blanked, same "don't silently drop what we don't recognize" policy as splitPlzCity below.
function mapGdtGender(raw: string | undefined): string | undefined {
    if (raw === "1") return "M";
    if (raw === "2") return "F";
    if (raw === "3" || raw === "0") return "D";
    return raw;
}

/** FK 3106 is one combined "PLZ Ort" field (e.g. "85646 Anzing"), not two separate
 *  fields — splitting it is real parsing, not a guess, since German PLZ is always 5
 *  digits. When a value doesn't match that shape, keep it visible as the city rather
 *  than silently dropping it or guessing a PLZ out of it. */
function splitPlzCity(raw: string | undefined): { plz?: string; city?: string } {
    if (!raw) return {};
    const match = raw.match(/^(\d{5})\s*(.*)$/);
    if (match) {
        return { plz: match[1], city: match[2].trim() || undefined };
    }
    return { city: raw };
}

function firstValue(fields: GdtField[], kennung: string): string | undefined {
    const f = fields.find((x) => x.kennung === kennung);
    return f ? f.value.trim() : undefined;
}

/** The first non-empty value among field ids that mean the same thing in different senders. */
function firstOf(fields: GdtField[], kennungen: string[]): string | undefined {
    for (const kennung of kennungen) {
        const value = firstValue(fields, kennung);
        if (value) return value;
    }
    return undefined;
}

function allValues(fields: GdtField[], kennung: string): string[] {
    return fields.filter((f) => f.kennung === kennung).map((f) => f.value.trim()).filter(Boolean);
}

/** Parses a .gdt File into the fields the patient-sync endpoint needs. Throws a
 *  GdtParseError (named after the file) on any malformed/truncated/unrecognizable
 *  content - never returns a partial/best-effort result silently. */
export async function parseGdtFile(file: File): Promise<ParsedGdtPatient> {
    const buffer = await file.arrayBuffer();
    const { fields, charset } = decodeFields(buffer, file.name);

    const satzart = firstValue(fields, FK.SATZART);
    if (satzart && !VALID_SATZARTEN.has(satzart)) {
        throw makeParseError(`Unexpected Satzart ${satzart} (expected 6301 or 6302) — not a patient record`, file.name);
    }

    const externalId = firstValue(fields, FK.PATIENT_ID);
    const lastName = firstValue(fields, FK.LAST_NAME);
    if (!externalId) throw makeParseError(`Missing required field ${FK.PATIENT_ID} (Patient-ID)`, file.name);
    if (!lastName) throw makeParseError(`Missing required field ${FK.LAST_NAME} (Nachname)`, file.name);

    // 3112/3113 (postcode and city apart) win over the combined 3106 for their own part.
    const combined = splitPlzCity(firstValue(fields, FK.PLZ_CITY));
    const plz = firstValue(fields, FK.PLZ) || combined.plz;
    const city = firstValue(fields, FK.CITY) || combined.city;
    const tel = [
        ...allValues(fields, FK.PHONE_GENERAL),
        ...allValues(fields, FK.PHONE_1),
        ...allValues(fields, FK.PHONE_2),
        ...allValues(fields, FK.PHONE_1_ALT),
        ...allValues(fields, FK.PHONE_2_ALT),
    ];
    const guardianLastName = firstValue(fields, FK.RECIPIENT_LAST_NAME);

    return {
        externalId,
        lastName,
        firstName: firstValue(fields, FK.FIRST_NAME),
        street: firstValue(fields, FK.STREET),
        plz,
        city,
        birthDate: parseGdtBirthDate(firstValue(fields, FK.BIRTHDATE)),
        gender: mapGdtGender(firstValue(fields, FK.GENDER)),
        title: firstValue(fields, FK.TITLE) || undefined,
        insuranceNumber: firstValue(fields, FK.INSURANCE_NUMBER),
        insuranceProvider: firstValue(fields, FK.INSURANCE_PROVIDER),
        guardian: guardianLastName
            ? {
                lastName: guardianLastName,
                firstName: firstValue(fields, FK.RECIPIENT_FIRST_NAME),
                street: firstValue(fields, FK.RECIPIENT_STREET),
                plz: firstValue(fields, FK.RECIPIENT_PLZ),
                city: firstValue(fields, FK.RECIPIENT_CITY),
                title: firstValue(fields, FK.RECIPIENT_TITLE),
                birthDate: parseGdtBirthDate(firstValue(fields, FK.RECIPIENT_BIRTHDATE)),
                gender: mapGdtGender(firstValue(fields, FK.RECIPIENT_GENDER)),
            }
            : undefined,
        email: firstOf(fields, [FK.EMAIL, FK.EMAIL_ALT]),
        tel: tel.length > 0 ? tel : undefined,
        source: {
            gdtVersion: firstValue(fields, FK.GDT_VERSION) || undefined,
            charsetDeclared: firstValue(fields, FK.ENCODING) || undefined,
            charsetUsed: charset,
            senderId: firstValue(fields, FK.SENDER_ID) || undefined,
        },
    };
}
